import React from "react";
import { AmplitudeEventSource } from "@analytics/Amplitude/AmplitudeEvents";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { useEvent } from "@hooks/useEvent";
import { ItemTypes } from "../../drag-and-drop/DragAndDropConstants";
import {
    CompleteTaskEvent,
    DeleteTaskEvent,
    DuplicateTaskEvent,
    StartTaskTimerEvent,
} from "../AmplitudeTaskManagementEvents";
import { AssignTaskTagEvent, RemoveTaskTagEvent } from "../AmplitudeTaskTagEvents";
import { useTaskListContext } from "../TaskListContext";
import { draggedTaskEvent$, selectedDetail$ } from "../TaskManagementGlobals";
import { ClientTaskItemType } from "../TaskManagementTypes";
import TaskItem from "./TaskItem";

export interface SingularTaskItemProps {
    /**
     * Index of the task item in the list.
     */
    readonly index: number;
    /**
     * Task Id (document id)
     */
    readonly taskId: string;
    /**
     * Client Id (local id)
     */
    readonly clientId: string;
    /**
     * Task item
     */
    readonly task: ClientTaskItemType;
    /**
     * Callback on key press
     */
    readonly handleKeyPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    /**
     * Indicates if task title field should be focused on
     */
    readonly isTaskInFocus: boolean;
    /**
     * Tell Amplitude where the event is triggered from
     */
    readonly amplitudeEventSource: AmplitudeEventSource;
}
export default function SingularTaskItem(props: SingularTaskItemProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const { updateTaskTitle, updateTaskTag, updateTaskStatus, deleteTask, duplicateTask, taskFocusEvent$ } =
        useTaskListContext();

    const setDraggedTaskEvent = useEvent(() => {
        if (props.taskId) {
            draggedTaskEvent$.value = {
                type: "singular",
                taskId: props.taskId,
            };
        }
    });

    return (
        <TaskItem
            task={props.task}
            isTaskInFocus={props.isTaskInFocus}
            recurIcon={false}
            taskMethods={{
                buildKey: () => props.taskId,
                getDndItem: () => ({
                    id: props.taskId,
                    type: ItemTypes.TASK,
                    data: props.task,
                }),
                updateTaskTitle: (title) => updateTaskTitle(props.taskId, title),
                updateTaskTag: (tagId) => updateTaskTag(props.taskId, tagId),
                updateTaskStatus: (status) => updateTaskStatus(props.taskId, status),
            }}
            events={{
                onClick: () => {
                    selectedDetail$.value = {
                        type: "singular",
                        taskId: props.taskId,
                    };
                },
                onFocus: () => {
                    taskFocusEvent$.value = {
                        type: "singular",
                        taskId: props.taskId,
                    };
                },
                onKeyPress: props.handleKeyPress,
                onDrag: setDraggedTaskEvent,
                onComplete: () => {
                    amplitudeService.logEvent(
                        new CompleteTaskEvent({
                            type: "singular",
                            source: props.amplitudeEventSource,
                            clientId: props.clientId,
                        }),
                    );
                },
                onTagAssign: (tagId) => {
                    amplitudeService.logEvent(
                        new AssignTaskTagEvent({
                            taskType: "singular",
                            taskId: props.taskId,
                            tagId,
                            source: "task-details",
                        }),
                    );
                },
                onTagRemove: () => {
                    amplitudeService.logEvent(
                        new RemoveTaskTagEvent({ taskType: "singular", taskId: props.taskId, source: "task-details" }),
                    );
                },
                onTimerStart: (duration) => {
                    amplitudeService.logEvent(
                        new StartTaskTimerEvent({
                            source: props.amplitudeEventSource,
                            taskDuration: duration,
                            taskId: props.taskId,
                        }),
                    );
                },
            }}
            contextMenu={[
                {
                    key: "edit",
                    label: "Edit",
                    onClick: () => {
                        selectedDetail$.value = {
                            type: "singular",
                            taskId: props.taskId,
                        };
                    },
                },
                {
                    key: "duplicate",
                    label: "Duplicate",
                    onClick: () => {
                        duplicateTask({ taskId: props.taskId });
                        amplitudeService.logEvent(new DuplicateTaskEvent());
                    },
                },
                {
                    key: "delete",
                    label: "Delete",
                    onClick: () => {
                        deleteTask(props.taskId);
                        amplitudeService.logEvent(new DeleteTaskEvent({ source: props.amplitudeEventSource }));
                    },
                },
            ]}
        />
    );
}
